/**
 * buildOfficeCliSrcDoc — assemble the officecli preview iframe's srcDoc (#620 Phase C).
 *
 * The View iframe is OPAQUE-ORIGIN (`sandbox="allow-scripts"`, no allow-same-origin),
 * so the parent can no longer reach into its document to theme it or restore the
 * saved scroll/sheet/cell. Instead the parent BAKES everything into the srcDoc string
 * here — never touching the frame's DOM:
 *   - a restrictive CSP <meta> (`default-src 'none'`) so a malicious cell that somehow
 *     runs inside the opaque frame still can't fetch, navigate, or phone home;
 *   - the dark-theme stylesheet, inline, when the workspace theme is dark;
 *   - `window.__pmInit` carrying the saved view state, which the in-iframe augment
 *     script reads on load to restore sheet/scroll/active cell (B4 view sync).
 * The augment script (added server-side in officecli-preview.ts) posts view-state
 * changes back OUT via postMessage — the one channel that survives opaque origin.
 */

/** Dark chrome override — only the editor CHROME goes dark; the document grid stays
 *  light (a white sheet on a dark frame), matching OnlyOffice. Baked inline by the
 *  parent so there is no theme flash and no same-origin DOM write. */
export const OFFICECLI_DARK_CSS = `
:root,html,body{background:#1e1e1e !important}
.sheet-slider,.sheet-tabs{background:#1e1e1e !important}
.sheet-content,.table-wrapper{background:#ffffff !important}
#__pmbar{background:#252526 !important;border-bottom-color:#3a3a3a !important;color:#e0e0e0 !important}
#__pmbar #__pmref{color:#4ec9a0 !important}
#__pmbar #__pmfx{color:#a8a8a8 !important}
#__pmstatus{background:#1b1b1c !important;border-top-color:#3a3a3a !important;color:#a8a8a8 !important}
.file-title{background:#1b5e3f !important;color:#e8f5e9 !important}
.sheet-tab{background:#2a2a2c !important;color:#bbb !important;border-color:#3a3a3a !important}
.sheet-tab.active{background:#ffffff !important;color:#1b1b1b !important;border-color:#3a3a3a !important}
`

const CSP_META =
  '<meta http-equiv="Content-Security-Policy" content="' +
  "default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; font-src data:" +
  '">'

export interface OfficeCliViewState {
  sheet?: number
  scrollLeft?: number
  scrollTop?: number
  activeCell?: string
}

export interface BuildSrcDocOptions {
  dark: boolean
  saved?: OfficeCliViewState
}

/** JSON for embedding in an inline <script>: escape `<` so a `</script>` in any value
 *  can't close our script tag (defense-in-depth; the values are our own data). */
function safeJson(v: unknown): string {
  return JSON.stringify(v ?? {}).replace(/</g, '\\u003c')
}

export function buildOfficeCliSrcDoc(html: string, opts: BuildSrcDocOptions): string {
  const head =
    CSP_META +
    `<script>window.__pmInit=${safeJson({ saved: opts.saved })}</script>` +
    (opts.dark ? `<style id="__pmtheme">${OFFICECLI_DARK_CSS}</style>` : '')

  // Insert right after <head ...> so the CSP + init run before the body + augment script.
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + head)
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => `${m}<head>${head}</head>`)
  return `<head>${head}</head>${html}`
}
